/**
 * The browser's whole telemetry surface — product analytics, session replay and
 * error tracking — loaded on demand (PLAN-REMAINING O1).
 *
 * One SDK for the three of them, which is the decision this file exists to
 * carry out. The client used to run `@sentry/browser` for crashes and nothing
 * for usage; that integration never had a DSN and was deleted rather than
 * pointed at a new one, because two client SDKs is two boot costs, two consent
 * stories and two dashboards that cannot link a crash to what the brewer was
 * doing when it happened. PostHog captures the exception *and* the replay, and
 * the issue is one click from the recording. The **Worker** is unchanged and is
 * still on `@sentry/cloudflare` (`worker/sentry.ts`): a server has no replay to
 * link to, and the SDK there is already scrubbed, tested and shipping.
 *
 * ## Why it is lazy, and why that is the same call `lib/paddle.ts` made
 *
 * posthog-js is ~200 KB of third-party JavaScript, and this is an app whose
 * whole promise is that it opens instantly and works offline. Importing it
 * statically would put an analytics vendor in the boot chunk — the identical
 * objection `lib/paddle.ts` raises about Paddle's CDN script, answered the
 * identical way: nothing is fetched until it is wanted, and the fetch is cached
 * in a module-level promise so repeated calls load it once.
 *
 * The trigger is the one the deleted Sentry loader used, because it is the
 * repo's rule for every credential: `/api/config` has to say there is a key.
 * **No key, no dynamic import** — not an import that then does nothing. A
 * deployment with no PostHog project pays nothing at all, which is every
 * development build, the whole test suite, and staging by default.
 *
 * "Nothing" includes the service worker, which is the half that is easy to
 * miss: the precache glob in `vite.config.ts` names every emitted chunk, so
 * without a carve-out this one would be downloaded to every brewer's phone on
 * their first visit regardless of whether anything would ever execute it. There
 * is a carve-out, and the comment beside it says so. The cost is that telemetry
 * needs the network the first time it loads, which is the correct trade for a
 * vendor SDK in an app that is otherwise entirely ours.
 *
 * ## Why the key is on `/api/config` even though it is not a secret
 *
 * A PostHog project API key is public by design, exactly like the Paddle client
 * token in `lib/paddle.ts`: it is an ingest address with write-only authority,
 * it is visible to everybody who loads the app, and there is nowhere in a
 * browser to hide a real secret. So publishing it is not the question. The
 * question is where it *comes from*, and the answer is the same one the whole
 * codebase gives: one configuration authority, and absence means off. A key
 * compiled into the bundle would make every local `pnpm dev`, every CI run and
 * every preview build send live events into the production project, and turning
 * that off would be a code change. Coming down the wire, silence is the default
 * and a deployment opts in by setting `POSTHOG_KEY`.
 *
 * ## What the replay is allowed to see: chrome, never content
 *
 * The launch posture was mask-everything. `maskAllInputs`, plus
 * `maskTextSelector: '*'` — rrweb's everything-selector — so that not one
 * rendered string reached the recorder. Shipping a recorder into a product
 * whose most private object is an unreleased recipe, that is the correct way
 * round to be wrong: over-masking costs data, under-masking costs a promise.
 *
 * Then the recordings were watched, and the verdict was that there was nothing
 * in them: *literally every string is starred out*. Not only the recipe names
 * and the batch notes, which is the point — the navigation, the tab labels,
 * the button captions, the empty-state copy, the dialog headings, the gravity
 * readings. A replay exists to show a brewer failing to find a button, and it
 * could not show *which* button, because every label on the page was a row of
 * asterisks. Masking the app's own chrome protected nobody: those strings are
 * ours, they are identical in every session, and they are on the marketing
 * site. It deleted the map and left the territory blank.
 *
 * So the steady state is targeted masking, and the target is **declared, not
 * guessed at**. {@link USER_CONTENT_ATTR} — `data-user-content` — is rendered
 * on the element whose text comes from the workspace or the account, and
 * `maskTextSelector` is that attribute and everything under it. Everything
 * else records as written. The rule for every surface built after this one is
 * one sentence long: **render user text, spread {@link userContent}**. It is
 * an attribute rather than a class so that it cannot be lost to a `cn()` merge
 * or a design refactor, and a single grep for the constant finds every call
 * site the promise depends on.
 *
 * `maskAllInputs` stays `true`, unconditionally and with no selector attached.
 * Anything a person types is user content by definition — the field they typed
 * it into is not evidence either way, and a new input added by someone who
 * never read this file must be masked before it is annotated, not after.
 *
 * Faces are the exception a text mask cannot cover: {@link USER_IMAGE_CLASS}
 * is posthog-js's own default `blockClass`, and an avatar `<img>` wearing it is
 * replaced by a placeholder box in the recording rather than uploaded.
 *
 * The invariant across the two layers, stated so a later change can be checked
 * against it: **event payloads contain no user text at all; replays show the
 * chrome and star the content.** Autocapture therefore keeps `mask_all_text`
 * as it was — the SDK's types are explicit that it governs `textContent` on
 * autocaptured elements and nothing in `session_recording` (they are separate
 * config paths, not one switch) — and an analytics click event has no use for
 * readable text of *either* kind. The attribute ignorelist covers the five
 * attributes that mirror rendered text. Structural attributes (`class`, `id`,
 * `href`, `data-*`) are kept, because without them an autocaptured click is a
 * tag name and nothing else.
 *
 * **The residual the selector cannot reach:** `maskTextSelector` masks text
 * *nodes*, and rrweb snapshots attributes as written. An `aria-label="Delete
 * Kölsch"` on an icon button therefore still travels, as it did under the
 * everything-selector — this change neither opens that hole nor closes it. It
 * is invisible in the player and it is a name rather than a note, so the fix
 * (`maskAttributeFn`, which would also blank every `placeholder` and `title`
 * the chrome uses) costs more readability than it buys privacy today. Prefer
 * an `aria-label` that names the action rather than the document when writing
 * a new one.
 *
 * What is deliberately *not* annotated is as much of the design as what is: the
 * public gallery and the shared-recipe page (published on purpose, already
 * readable by strangers), catalog ingredient names (our data, rendered from
 * `@beerwright/catalog`), and numbers — an OG of 1.048 identifies nobody, and
 * starring the vitals out was half of what made the first replays worthless.
 *
 * `before_send` then truncates every URL-bearing property at the `?`, which is
 * the one scrubbing rule inherited wholesale from the Sentry module it replaces
 * and for the same reason: `/api/auth/magic-link/verify?token=…` carries a
 * single-use sign-in token, and a token in an analytics event is a sign-in link
 * in an analytics event. Campaign attribution survives it, because PostHog
 * lifts `utm_*` into properties of their own before this runs.
 *
 * **The residual risk, stated plainly:** rrweb writes the document's own `href`
 * into the replay's first snapshot, and this hook cannot reach that. A replay
 * that begins on a magic-link redirect can therefore contain the URL that
 * redirected it. It is a narrow window — the token is single-use and already
 * spent by the time the app boots — but it is not zero, and it is the reason
 * replays are treated as data with a retention policy rather than as logs.
 *
 * ## Do Not Track
 *
 * `respect_dnt: true`. A browser that asks not to be tracked is not tracked,
 * including by the replay, and this is the one option here that costs real data
 * for nothing but the asking being honoured.
 *
 * ## Identity is an id and nothing else
 *
 * {@link setAnalyticsIdentity} calls `identify(userId)` with **no** person
 * properties: no email, no name, no plan. The account id is an opaque string
 * that means something only against this deployment's database, and it is
 * enough to join a session to an account when a bug report says "it happens
 * every time I open my IPA". Everything else is PII that PostHog does not need
 * in order to answer the questions it is here to answer.
 *
 * `reset()` on sign-out, and *only* on a genuine sign-out: resetting on an
 * ordinary guest boot would rotate the anonymous id and split one visitor into
 * a new person on every page load, so the transition is tracked in module state
 * and a guest who was already a guest is left alone.
 *
 * ## Pageviews come from the router
 *
 * posthog-js captures one `$pageview` at `init` and then nothing, because it is
 * a script on a page and this is a single-page app: every navigation after the
 * first happens inside TanStack Router with no page load for the SDK to see.
 * {@link trackPageviews} subscribes to the router once — router-side, not one
 * hook per route — and captures a `$pageview` per resolved navigation, deduped
 * by pathname so that a search-param edit (the gallery's filters write to the
 * URL on every keystroke) is not a hundred pageviews. `'history_change'` would
 * cover some of this without the subscription, but it patches `history` behind
 * the router's back to learn what the router already knows and tells us.
 *
 * ## The gap this cannot close, and the one it inherits
 *
 * Reporting starts after `/api/config` answers. A crash *before* that has
 * nowhere to go, because there is no key yet to send it to;
 * {@link reportError} therefore queues rather than dropping, and the queue is
 * drained when the SDK arrives. A crash that beats the config request by a long
 * way is still lost, and closing that would mean shipping a key in the bundle,
 * which is the thing this design refuses.
 *
 * ## Why there is no `posthog-sdk.ts`, when there was a `sentry-sdk.ts`
 *
 * That indirection was worth 100 KB: `@sentry/browser` exports a tree of
 * independent functions, and a namespace `import()` forced the bundler to keep
 * all of them, so naming the two that were used in a static re-export cut the
 * chunk from 142 KB gzipped to 29. posthog-js is the opposite shape — its
 * entire API hangs off one singleton object, which either ships or does not —
 * so the same trick would buy nothing and cost a file. The mock seam it also
 * provided is not needed either: `posthog.test.ts` mocks `posthog-js` itself
 * and counts module evaluations, which is the assertion that matters.
 *
 * ## No DOM, no analytics
 *
 * Every entry point is inert without a `window`, so importing this module in a
 * node test is safe. The client suite mocks `posthog-js`; nothing in it loads
 * the real SDK and nothing in it reaches posthog.com.
 */

import type { CaptureResult, PostHogConfig, Properties } from 'posthog-js';

/**
 * The browser's half of `/api/config`'s `analytics` block.
 *
 * Mirrors `worker/env.ts`'s `AnalyticsConfig`, and `null` there means "this
 * deployment has no telemetry" rather than an object with an empty key — which
 * is what makes {@link startAnalytics} a single early return.
 */
export interface AnalyticsConfig {
  /** The project API key (`phc_…`). Public by design — see above. */
  readonly key: string;
  /** The ingest host. The Worker defaults it to PostHog's US cloud. */
  readonly host: string;
}

/**
 * The attribute that marks an element as the user's own text.
 *
 * Rendered on the *smallest* element that carries workspace or account text —
 * the recipe's name, not the row it sits in; a settings row's value, not its
 * label — because the recorder masks the annotated element and everything
 * beneath it, and a container annotated too high stars out the chrome around
 * the content and puts us back where the launch config was.
 *
 * Exported as a name rather than written out at the call sites so that
 * `USER_CONTENT_ATTR` and {@link userContent} between them are one grep for
 * every surface the privacy promise rests on.
 */
export const USER_CONTENT_ATTR = 'data-user-content';

/**
 * The attribute as spreadable props: `<span {...userContent}>{recipe.name}</span>`.
 *
 * The whole convention for annotating a new surface. It has no value because
 * `[data-user-content]` matches on presence, and an empty string is what React
 * renders for a bare HTML attribute.
 */
export const userContent = { [USER_CONTENT_ATTR]: '' } as const;

/**
 * The selector the recorder masks: the annotated element and its subtree.
 *
 * Both halves are needed. rrweb matches text nodes against this selector by
 * their *parent element*, so `[data-user-content]` alone would mask a bare
 * `<span {...userContent}>{name}</span>` but not the same name wrapped one
 * level deeper in an `<em>` or a formatting component. The descendant half
 * closes that, and the cost of the pair is that annotating a container is a
 * heavier act than it looks — see {@link USER_CONTENT_ATTR}.
 */
const USER_CONTENT_SELECTOR = `[${USER_CONTENT_ATTR}], [${USER_CONTENT_ATTR}] *`;

/**
 * The class that keeps an image out of the recording entirely.
 *
 * posthog-js's own default `blockClass`, verified against the pinned 1.418.5
 * types, which is why it needs no entry in the config below: rrweb replaces a
 * blocked element with a placeholder of the same size and never uploads its
 * `src`. For avatars, which is the one piece of user content a *text* mask
 * cannot touch — a face is not a string — and which are as often as not a
 * `data:` URL or a Google profile photo.
 *
 * The trade, stated because it is a real one: the same class also makes
 * autocapture ignore a click that lands on the image, so an avatar inside a
 * button loses that click when the pointer hits the picture rather than the
 * label beside it. A blocked face is worth more than a duplicate click event.
 */
export const USER_IMAGE_CLASS = 'ph-no-capture';

/** The slice of the SDK this module uses after `init`. No wider on purpose. */
interface PostHogApi {
  init(key: string, options: Partial<PostHogConfig>): unknown;
  capture(event: string): unknown;
  captureException(error: unknown, additionalProperties?: Properties): unknown;
  identify(distinctId: string): void;
  reset(): void;
}

/**
 * The one in-flight-or-finished load.
 *
 * Module-level, so a second call awaits the first rather than initialising the
 * SDK twice — and so a failed load (an offline tab, a blocked chunk, an ad
 * blocker, which for an analytics vendor is the common case) is retried on the
 * next call instead of poisoning every later one. Exactly the shape
 * `lib/paddle.ts` uses, for exactly the same reasons.
 */
let loading: Promise<PostHogApi> | null = null;

/**
 * The account this session belongs to, as far as the app has said.
 *
 * Kept here rather than read back off the SDK because it is known before the
 * SDK exists — `/api/me` and `/api/config` are fetched in parallel and either
 * may win — and because the *transition* is what decides whether `reset()` is
 * called. See {@link setAnalyticsIdentity}.
 */
let identity: string | null = null;

/** The pathname the last `$pageview` was captured for. See {@link trackPageviews}. */
let lastPathname: string | null = null;

/**
 * Errors raised before the SDK finished loading.
 *
 * Bounded, because an app that is failing in a render loop can produce
 * thousands of these in a second and an unbounded array would turn a bug into
 * an out-of-memory crash. The first few are the interesting ones anyway: what
 * is wanted is the *first* failure, not the hundredth consequence of it.
 */
const pending: { readonly error: unknown; readonly properties: Properties | undefined }[] = [];

/** How many pre-load errors are worth keeping. */
const MAX_PENDING = 10;

/**
 * Properties that carry a URL, and are therefore truncated at the `?`.
 *
 * A fixed list rather than "anything that looks like a URL": guessing would
 * mangle a legitimate string property one day, and these five are every place
 * posthog-js puts an address the app has visited or linked to.
 */
const URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$external_click_url',
] as const;

/**
 * Attributes autocapture must not read, because they mirror rendered text.
 *
 * `mask_all_text` already stops `textContent` from riding along; these are the
 * places the same sentence hides when it is written for a screen reader or a
 * tooltip instead. Everything structural — `class`, `id`, `href`, `data-*` — is
 * deliberately still captured, or a click event would identify nothing.
 */
const MASKED_ATTRIBUTES = ['title', 'aria-label', 'alt', 'placeholder', 'value'];

/** Whether this environment can report at all. */
function hasDom(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Turn on analytics, replay and error tracking, if this deployment has any.
 *
 * Called once the session provider has `/api/config`. Idempotent and cheap to
 * call again — the module-level promise is the whole of the memoisation — so a
 * caller may put it in an effect without guarding it.
 *
 * @param config - `session.config.analytics`. `null` means no key, which means
 *   this function does nothing and imports nothing.
 * @returns A promise that settles when the SDK is up and anything queued has
 *   been handed over. It never rejects — a chunk that will not load is a failed
 *   *diagnostic*, never something to tell the user about — so the app calls
 *   this and walks away. It is returned rather than swallowed only so the test
 *   suite has something to await instead of guessing at microtask counts.
 */
export function startAnalytics(config: AnalyticsConfig | null): Promise<void> {
  if (config === null || !hasDom()) return Promise.resolve();
  loading ??= initialize(config).catch((error: unknown) => {
    // Cleared so a tab that was offline when the chunk was requested can try
    // again on the next call rather than staying silent for its lifetime.
    loading = null;
    throw error;
  });
  return loading.then(drain).catch(() => undefined);
}

async function initialize(config: AnalyticsConfig): Promise<PostHogApi> {
  // The dynamic import: Vite emits it as its own chunk, and this line is the
  // only thing in the app that asks for it.
  const { posthog } = await import('posthog-js');
  posthog.init(config.key, {
    api_host: config.host,
    // Clicks, form submissions and changes, with the text and the text-bearing
    // attributes stripped out. See MASKED_ATTRIBUTES.
    autocapture: { element_attribute_ignorelist: MASKED_ATTRIBUTES },
    // Autocapture only, and unrelated to the recorder's masking below: no click
    // event carries the `textContent` of what was clicked, chrome included. An
    // event payload has no use for a readable label; a replay does.
    mask_all_text: true,
    // The initial load, and only that: the SPA's own navigations arrive through
    // trackPageviews, which is why this is `true` rather than 'history_change'.
    capture_pageview: true,
    // Explicit rather than inherited from `capture_pageview`, so that changing
    // one of the two above never silently turns off how long a page was open.
    capture_pageleave: true,
    // Error tracking: unhandled errors and unhandled promise rejections. React
    // render errors reach neither and come through reportError instead — see
    // `components/error-boundary.tsx`. Console errors stay off (the SDK's
    // default), because this app logs deliberately and loudly.
    capture_exceptions: true,
    // A browser that asks not to be tracked is not tracked.
    respect_dnt: true,
    // Advertising ids and click ids out of the URL, on top of the truncation in
    // `before_send`.
    mask_personal_data_properties: true,
    // The SDK's own default, stated because it is load-bearing here: an
    // anonymous visitor gets no person profile at all, and one is created only
    // when setAnalyticsIdentity names an account.
    person_profiles: 'identified_only',
    session_recording: {
      /** Every input, whatever its type. Typing is user content by definition. */
      maskAllInputs: true,
      /** Only what the app has declared as the user's own. See USER_CONTENT_ATTR. */
      maskTextSelector: USER_CONTENT_SELECTOR,
    },
    before_send: scrub,
  });
  // The identity, if the session resolved before the config did. Also the point
  // at which the SDK's own initial $pageview has been captured, so the router
  // subscription must not capture the same pathname again.
  if (identity !== null) posthog.identify(identity);
  lastPathname = window.location.pathname;
  return posthog;
}

/**
 * The last thing that touches an event before it leaves the browser.
 *
 * Exported for the test suite, which asserts it as the pure function it is.
 * `null` is posthog-js's own "this event was dropped by an earlier hook", and
 * is passed through untouched.
 */
export function scrub(event: CaptureResult | null): CaptureResult | null {
  if (event === null) return null;
  stripUrls(event.properties);
  stripUrls(event.$set);
  stripUrls(event.$set_once);
  return event;
}

/** Truncate every known URL property of one property bag at the `?`. */
function stripUrls(properties: Properties | undefined): void {
  if (properties === undefined) return;
  for (const name of URL_PROPERTIES) {
    const value: unknown = properties[name];
    if (typeof value === 'string') properties[name] = stripQuery(value);
  }
}

/** Everything up to the first `?`. Never throws; the input may not be a URL. */
function stripQuery(url: string): string {
  const cut = url.indexOf('?');
  return cut === -1 ? url : url.slice(0, cut);
}

/**
 * Report an error the app caught itself.
 *
 * The counterpart of the SDK's global handlers, for the failures that never
 * reach `window.onerror`: React renders them into an error boundary instead
 * (see `components/error-boundary.tsx`). Safe to call before — or entirely
 * without — {@link startAnalytics}: it queues, and drops silently once the
 * queue is full or the deployment turns out to have no key.
 *
 * @param properties - Extra fields to hang on the exception, for the reports
 *   whose message alone does not say *which* thing went wrong. They ride the
 *   pre-load queue with the error, so a crash that beats the SDK keeps them.
 */
export function reportError(error: unknown, properties?: Properties): void {
  if (!hasDom()) return;
  if (loading === null) {
    if (pending.length < MAX_PENDING) pending.push({ error, properties });
    return;
  }
  void loading
    .then((posthog) => posthog.captureException(error, properties))
    .catch(() => undefined);
}

/** Hand the SDK whatever failed while it was still loading. */
function drain(posthog: PostHogApi): void {
  for (const { error, properties } of pending.splice(0, pending.length)) {
    posthog.captureException(error, properties);
  }
}

/**
 * Tell PostHog which account this session belongs to, or that it no longer has
 * one.
 *
 * The id and nothing else — no email, no name, no plan (see the header). Safe
 * before the SDK exists: the answer is remembered and applied at `init`.
 *
 * @param userId - `session.user?.id ?? null`. Call it with `null` on sign-out.
 *   A guest who was always a guest is a no-op, deliberately: `reset()` rotates
 *   the anonymous id, and calling it on every boot would turn one returning
 *   visitor into a new person every time.
 */
export function setAnalyticsIdentity(userId: string | null): void {
  if (!hasDom() || userId === identity) return;
  const wasIdentified = identity !== null;
  identity = userId;
  if (loading === null) return;
  void loading
    .then((posthog) => {
      if (userId !== null) posthog.identify(userId);
      else if (wasIdentified) posthog.reset();
    })
    .catch(() => undefined);
}

/** Anything that can be subscribed to for resolved navigations. */
export interface RouterLike {
  /** TanStack Router's `subscribe`, narrowed to the one event this needs. */
  subscribe(eventType: 'onResolved', listener: () => void): () => void;
}

/**
 * Capture a `$pageview` for every SPA navigation, for the life of the app.
 *
 * Called once, next to the router it is given (`main.tsx`), rather than from a
 * component: the router outlives every route that renders under it, and one
 * subscription cannot be forgotten in a route that does not opt in.
 *
 * Navigations that happen before the SDK loads are dropped rather than queued.
 * They are the boot sequence, and the `$pageview` `init` captures covers where
 * the brewer actually landed.
 *
 * @returns The router's own unsubscribe, so a test can undo it.
 */
export function trackPageviews(router: RouterLike): () => void {
  if (!hasDom()) return () => undefined;
  return router.subscribe('onResolved', capturePageview);
}

function capturePageview(): void {
  if (loading === null) return;
  const { pathname } = window.location;
  // Deduped by pathname, which is what posthog-js's own history autocapture
  // does: the gallery writes its filters into the query string as they are
  // typed, and each of those is a resolved navigation.
  if (pathname === lastPathname) return;
  lastPathname = pathname;
  void loading.then((posthog) => posthog.capture('$pageview')).catch(() => undefined);
}

/**
 * Forget that the SDK was ever loaded. **Tests only.**
 *
 * The memoised promise, the identity and the last pathname are all module
 * state, and a suite that asserted "no key, no import" would otherwise be
 * order-dependent on the one that asserts the opposite.
 */
export function resetAnalyticsForTests(): void {
  loading = null;
  identity = null;
  lastPathname = null;
  pending.length = 0;
}
